import React from 'react';
import { useCalendar } from '../../hooks/useCalendar';
import { useUnavailableDates } from '../../hooks/useUnavailableDates';
import CalendarModal from './CalendarModal';
import CalendarLegend from './CalendarLegend';

const unavailableDatesEnabled = window.wwoofBooking?.unavailableDatesEnabled || false;
const wwooferInitiatedEnabled = window.wwoofBooking?.settings?.wwooferInitiatedEnabled || false;

const CalendarView = ({ 
  bookings, 
  calendarModal, 
  setCalendarModal,
  calendarView,
  actionLoading,
  onEditDates,
  onCancel,
  onAccept,
  currentUserId
}) => {
  const [pendingAdd, setPendingAdd] = React.useState(null); // { startDate, endDate }
  const [pendingRemove, setPendingRemove] = React.useState(null); // unavailableId
  const [unavailableError, setUnavailableError] = React.useState(null);

  const {
    unavailableDates,
    addUnavailableRange,
    removeUnavailableRange,
  } = useUnavailableDates(unavailableDatesEnabled ? currentUserId : null);

  const handleSelectDates = unavailableDatesEnabled
    ? (startDate, endDate) => {
        setUnavailableError(null);
        setPendingAdd({ startDate, endDate });
      }
    : null;

  const handleUnavailableEventClick = unavailableDatesEnabled
    ? (unavailableId) => {
        setUnavailableError(null);
        setPendingRemove(unavailableId);
      }
    : null;

  const calendarRef = useCalendar(
    bookings,
    (bookingId) => {
      const booking = bookings.find(b => b.id == bookingId);
      if (booking) {
        setCalendarModal({ booking });
      }
    },
    calendarView,
    unavailableDates,
    handleSelectDates,
    handleUnavailableEventClick
  );

  // Close modal if the booking no longer exists after data refresh
  React.useEffect(() => {
    if (calendarModal && !bookings.find(b => b.id == calendarModal.booking.id)) {
      setCalendarModal(null);
    }
  }, [bookings, calendarModal]);

  // Update modal booking data when bookings array changes
  React.useEffect(() => {
    if (calendarModal) {
      const updatedBooking = bookings.find(b => b.id == calendarModal.booking.id);
      if (updatedBooking) {
        // Compare booking objects by status and other key properties to avoid unnecessary updates
        const hasStatusChanged = updatedBooking.status !== calendarModal.booking.status;
        const hasStartDateChanged = updatedBooking.start_date !== calendarModal.booking.start_date;
        const hasEndDateChanged = updatedBooking.end_date !== calendarModal.booking.end_date;
        
        if (hasStatusChanged || hasStartDateChanged || hasEndDateChanged) {
          setCalendarModal({ booking: updatedBooking });
        }
      }
    }
  }, [bookings, calendarModal]);

  const confirmAddUnavailable = async () => {
    if (!pendingAdd) return;
    try {
      await addUnavailableRange(pendingAdd.startDate, pendingAdd.endDate);
    } catch (err) {
      setUnavailableError(err.message || 'Failed to mark dates as unavailable.');
    }
    setPendingAdd(null);
  };

  const confirmRemoveUnavailable = async () => {
    if (!pendingRemove) return;
    try {
      await removeUnavailableRange(pendingRemove);
    } catch (err) {
      setUnavailableError(err.message || 'Failed to remove unavailability.');
    }
    setPendingRemove(null);
  };

  return (
    <div className='calendar-view'>
      <div ref={calendarRef} />
      <CalendarLegend unavailableDatesEnabled={unavailableDatesEnabled} wwooferInitiatedEnabled={wwooferInitiatedEnabled} />
      {unavailableError && (
        <div className="unavailable-error" style={{ color: '#d63638', marginTop: '8px' }}>
          {unavailableError}
        </div>
      )}
      {pendingAdd && (
        <div className="unavailable-confirm-modal">
          <div className="unavailable-confirm-modal__overlay" onClick={() => setPendingAdd(null)} />
          <div className="unavailable-confirm-modal__box">
            <h3>Mark as Unavailable?</h3>
            <p>Mark <strong>{pendingAdd.startDate}</strong> to <strong>{pendingAdd.endDate}</strong> as unavailable?</p>
            <div className="unavailable-confirm-modal__actions">
              <button className="button button-primary" onClick={confirmAddUnavailable}>Confirm</button>
              <button className="button" onClick={() => setPendingAdd(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      {pendingRemove && (
        <div className="unavailable-confirm-modal">
          <div className="unavailable-confirm-modal__overlay" onClick={() => setPendingRemove(null)} />
          <div className="unavailable-confirm-modal__box">
            <h3>Remove Unavailability?</h3>
            <p>Mark these dates as available again?</p>
            <div className="unavailable-confirm-modal__actions">
              <button className="button button-primary" onClick={confirmRemoveUnavailable}>Yes, Remove</button>
              <button className="button" onClick={() => setPendingRemove(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      {calendarModal && (
        <CalendarModal
          booking={calendarModal.booking}
          onClose={() => setCalendarModal(null)}
          actionLoading={actionLoading}
          onEditDates={onEditDates}
          onCancel={onCancel}
          onAccept={onAccept}
          currentUserId={currentUserId}
        />
      )}
    </div>
  );
};

export default CalendarView;
